import { useEffect, useRef, useState } from "react";

import { Destination, ModelConfig } from "@prequel/react";
import { Checkbox } from "@prequel/shared-components";

import { useModelsForRecipient } from "@/api/queries/models";
import { ALL_CURRENT_FUTURE_MODELS } from "@/types";

type ModelsProps = {
  recipientId?: string;
  destination: Destination;
  setDestinationField: (key: keyof Destination, value: string | boolean | string[]) => void;
  modelsError?: string;
  disabled: boolean;
  isEditing: boolean;
};
const Models = ({ recipientId, destination, setDestinationField, modelsError, disabled, isEditing }: ModelsProps) => {
  const [availableModels, setAvailableModels] = useState<ModelConfig[]>();
  // enabledModels is only for keeping track of previous state when all_models_present_future is unselected
  const [enabledModels, setEnabledModels] = useState<string[]>();
  const { data: models } = useModelsForRecipient(recipientId);
  const allFutureModels = JSON.stringify(destination.enabled_models) === JSON.stringify(ALL_CURRENT_FUTURE_MODELS);

  // Track which recipient we last initialized enabled models for, so that query refetches
  // (eg on network reconnect once the data is stale) don't clobber the user's selection with
  // the all-models wildcard.
  const initializedRecipientRef = useRef<string | undefined>(undefined);

  // Whenever the models change (either on initialization or because of a recipient-change), update
  // the enabled models. Note that in edit mode, enabled models are dictated by the destination state.
  // Note that models are automatically refetched when the recipient changes based on how the queryKey was defined.
  useEffect(() => {
    setAvailableModels(models);
    // If not in edit mode (ie in destination add mode), initially enable all models when the recipient's
    // models first load or when the recipient changes.
    // If in edit mode, the source of truth is the destination.enabled_models.
    if (!isEditing && !!models && initializedRecipientRef.current !== recipientId) {
      initializedRecipientRef.current = recipientId;
      const enabled = models.map(({ model_name }) => model_name);
      setEnabledModels(enabled);
      setDestinationField("enabled_models", ALL_CURRENT_FUTURE_MODELS);
    }
  }, [models, recipientId, isEditing, setDestinationField]);

  const updateModels = (isEnabled: boolean, modelName: string) => {
    // If a model's checkbox is checked, add it to the list of enabled models. If it's unchecked, remove it.
    const updatedModels = isEnabled
      ? [...(destination.enabled_models ?? []), modelName]
      : (destination.enabled_models?.filter((m) => m !== modelName) ?? []);

    setDestinationField("enabled_models", updatedModels);
    setEnabledModels(updatedModels);
  };

  return (
    <>
      {availableModels && availableModels.length > 0 && (
        <>
          <label className="block text-sm font-medium text-gray-700">
            Select what models the destination will receive
          </label>
          {availableModels.map(({ model_name, description }) => (
            <Checkbox
              key={model_name}
              id={model_name}
              label={model_name}
              description={description}
              checked={destination.enabled_models?.includes(model_name) || allFutureModels}
              setChecked={(isChecked) => updateModels(isChecked, model_name)}
              disabled={disabled || allFutureModels}
            />
          ))}
          <Checkbox
            key={"all_models_present_future"}
            id={"all_models_present_future"}
            label={"All current and future models"}
            description={"Any model added to these products in the future will be synced"}
            checked={allFutureModels}
            setChecked={(isChecked: boolean) =>
              isChecked
                ? setDestinationField("enabled_models", ALL_CURRENT_FUTURE_MODELS)
                : setDestinationField("enabled_models", enabledModels ?? [])
            }
            disabled={disabled}
          />
        </>
      )}
      {modelsError && <p className="mt-1 text-xs font-medium text-red-600">{modelsError}</p>}
    </>
  );
};

export default Models;
